import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getProjectClaims } from '../../services/api'
import { resolveFocusIndex } from './claimFocus'
import HelpPopover from '../help/HelpPopover'
import ClaimRow from './ClaimRow'
import ClaimDrawer from './ClaimDrawer'

/**
 * The Claims lens (figure S1) — every claim this invention asserts, each with
 * the documents that support it and the score it was given against them.
 *
 * ONE DATA SOURCE, DELIBERATELY
 * -----------------------------
 * Everything here comes from getProjectClaims and nothing else: never the
 * page's documents query, never the per-document novelty_report.claim_analysis
 * that the document page renders. The cross-document edge is computed at read
 * time over the READER's entitled documents and re-filtered per reader on that
 * route alone. Assemble it from any other source and a document the reader is
 * not entitled to can appear inside a claim's support — the Bug #5635 shape. A
 * source test under src/screens/ pins that this file's import closure stays
 * clean of the documents fetcher, and the score, the prior art and the
 * assessment now ride on that same route for exactly the same reason.
 *
 * THE HEADING RENDERS UNCONDITIONALLY
 * -----------------------------------
 * Loading, empty and error all still show "Claim analysis". A lens that renders
 * nothing until data arrives gives the reader a blank box with no explanation.
 * On empty or error it says so honestly; it never invents a claim.
 */
export interface ClaimsLensProps {
  projectId: string
  /** 1-based claim the drawer is open on (`?focus=claim:N`), or null. */
  focusIndex?: number | null
  /** The reader closed the drawer (`?focus=none`) — do not re-open it. */
  closed?: boolean
  onFocus?: (index: number | null) => void
  /** The invention summary's key points, from the project analysis the page holds. */
  keyPoints?: string[]
}

export default function ClaimsLens({ projectId, focusIndex, closed, onFocus, keyPoints }: ClaimsLensProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['project-claims', projectId],
    queryFn: () => getProjectClaims(projectId),
    enabled: !!projectId,
    retry: false,
  })

  const rows = data?.claims ?? []
  // Bug #5835: the names come from the SAME payload as the ids, which is the
  // only source this lens may use. When the route carries no name for an id the
  // chip says so rather than printing the UUID.
  const documentNames = data?.documentNames ?? {}
  const supportingDocs = new Set(rows.flatMap((c) => c.supporting_document_ids))
  const totalDocs = data?.documentCount ?? 0
  // The drawer is half this screen, so a claim is always open: the URL's, or
  // the one defaultFocusIndex picks. Same rule the page uses for the You're-at
  // line, so the rail and the drawer can never name different claims.
  const focused = closed ? null : resolveFocusIndex(rows, focusIndex ?? null)

  // The list is NOT a scroll box. It used to be capped at 46rem with its own
  // scrollbar, which hid six of fourteen claims — including the one the
  // next-step banner names, the only place the "no supporting document"
  // warning appears. A claim a reader is told to act on has to be on the page.
  // So the whole list renders and the PAGE scrolls, as the figure draws it,
  // and the open claim is brought into view rather than scrolled to inside a
  // box that no longer exists.
  const listRef = useRef<HTMLOListElement>(null)
  useEffect(() => {
    if (focused == null) return
    const el = listRef.current?.children[focused - 1] as HTMLElement | undefined
    // jsdom has no layout, so scrollIntoView is a no-op there rather than a
    // crash; guarded anyway because older jsdom versions do not define it.
    el?.scrollIntoView?.({ block: 'nearest' })
  }, [focused, rows.length])

  return (
    <div data-part="ClaimsLens" className="grid items-start gap-4 xl:grid-cols-[1fr_412px]">
      <section className="light-card p-4">
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 border-l-4 border-l-amber-400 bg-amber-50 px-3 py-2 text-[11.5px] leading-snug text-amber-800">
          <span aria-hidden>⚠️</span>
          <p>
            <b>AI-assisted analysis — verify before relying.</b> Not a novelty ruling, clearance
            opinion or legal advice.
          </p>
        </div>

        {/* The figure puts the sub-line RIGHT-ALIGNED on the heading's own row.
            With flex-wrap it dropped onto the line below at this column width,
            so the row no longer wraps: the heading holds its width and the
            sub-line wraps inside itself, still right-aligned beside it. */}
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <h3 className="flex shrink-0 items-center gap-1.5 text-[13px] font-bold text-iflow-900">
            <span aria-hidden>🔎</span> <span>Claim analysis</span>
            <HelpPopover
              topic="concept-claim-score"
              title="How a claim is scored"
              body="One label scale for every claim: Highly novel / Moderately novel / Limited novelty / Low novelty, on the 0–100 scale. A claim is scored against the disclosure that supports it — all of it, not any one file."
              size={14}
            />
          </h3>
          <p className="min-w-0 text-right text-[11.5px] text-gray-500">
            {isLoading
              ? 'Reading the claims in this invention…'
              : `${rows.length} claim${rows.length === 1 ? '' : 's'} · drawn from ${supportingDocs.size}`
                // The denominator is this reader's own entitled total; without it
                // (a portal ahead of the Lambda) the line still reads correctly.
                + `${totalDocs ? ` of this invention's ${totalDocs}` : ''} document`
                + `${(totalDocs || supportingDocs.size) === 1 ? '' : 's'} · each scored against the `
                + 'disclosure that supports it'}
          </p>
        </div>

        {isError && (
          <p className="text-[11.5px] text-red-700">
            The claims for this invention could not be read just now. Nothing is missing from your
            invention — this view failed to load. Reload the page to try again.
          </p>
        )}

        {!isLoading && !isError && rows.length === 0 && (
          <p className="text-[11.5px] text-gray-500">
            No claims have been extracted from this invention’s documents yet. They appear here once
            a document has been analysed.
          </p>
        )}

        {rows.length > 0 && (
          <ol ref={listRef} className="space-y-2">
            {rows.map((claim, i) => (
              <li key={`${i}-${claim.claim}`}>
                <ClaimRow
                  claim={claim}
                  index={i + 1}
                  documentNames={documentNames}
                  focused={focused === i + 1}
                  onFocus={() => onFocus?.(focused === i + 1 ? null : i + 1)}
                />
              </li>
            ))}
          </ol>
        )}

        {keyPoints && keyPoints.length > 0 && (
          <details className="mt-3 rounded-lg border border-sky-200 bg-slate-50 px-3 py-2">
            <summary className="cursor-pointer text-[12.5px] font-bold text-iflow-900">
              Key points from the summary{' '}
              <span className="text-[11.5px] font-normal text-gray-500">
                · {keyPoints.length} point{keyPoints.length === 1 ? '' : 's'}
                {rows.length > 0 ? ' · collapsed while claim analysis is available' : ''}
              </span>
            </summary>
            <ul className="ml-4 mt-1.5 list-disc">
              {keyPoints.map((point) => (
                <li key={point} className="text-[11.5px] leading-relaxed text-gray-700">
                  {point}
                </li>
              ))}
            </ul>
          </details>
        )}

        <p className="mt-1.5 text-[11px] text-gray-500">
          Scored relative to the loaded corpus — coverage grows as more patents and journals are
          ingested.
        </p>
      </section>

      {focused != null && (
        <ClaimDrawer
          claim={rows[focused - 1]}
          index={focused}
          total={rows.length}
          documentNames={documentNames}
          onClose={() => onFocus?.(null)}
        />
      )}
    </div>
  )
}
